"use client";

/**
 * Error boundary GLOBAL (Next App Router). Ne se déclenche QUE si le layout
 * racine lui-même échoue : il REMPLACE alors complètement ce layout, donc il
 * doit rendre ses propres <html> et <body> et ne peut s'appuyer sur RIEN de
 * l'app (ni Header/Footer, ni providers, ni — par prudence — le CSS global).
 *
 * → Volontairement AUTONOME : styles inline, localisation dérivée de
 * `location.pathname` au montage (pas de `usePathname`, qui dépend du contexte
 * routeur potentiellement indisponible ici). FR / EN / AR (MSA), RTL inclus.
 * Doit être un Client Component (convention Next).
 */

import { useEffect } from "react";

type Lang = "fr" | "en" | "ar";

const COPY: Record<
  Lang,
  { title: string; body: string; retry: string; home: string; homeHref: string }
> = {
  fr: {
    title: "Une erreur est survenue",
    body: "Un incident technique a interrompu le chargement. Vous pouvez réessayer ou revenir à l'accueil.",
    retry: "Réessayer",
    home: "Retour à l'accueil",
    homeHref: "/",
  },
  en: {
    title: "Something went wrong",
    body: "A technical issue interrupted loading. You can try again or go back to the homepage.",
    retry: "Try again",
    home: "Back to home",
    homeHref: "/en",
  },
  ar: {
    title: "حدث خطأ ما",
    body: "حدث خلل تقني أثناء التحميل. يمكنك إعادة المحاولة أو العودة إلى الصفحة الرئيسية.",
    retry: "إعادة المحاولة",
    home: "العودة إلى الصفحة الرئيسية",
    homeHref: "/ar",
  },
};

const detectLang = (pathname: string): Lang => {
  if (pathname === "/ar" || pathname.startsWith("/ar/")) return "ar";
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  return "fr";
};

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}): JSX.Element {
  // `global-error` ne rend QUE côté client (il prend le relais après l'échec du
  // layout racine) : on peut donc dériver la langue directement de `location`,
  // sans état ni effet de synchronisation. `typeof window` garde le composant
  // sûr si jamais il était évalué côté serveur (repli FR).
  const lang: Lang =
    typeof window === "undefined" ? "fr" : detectLang(window.location.pathname);

  useEffect(() => {
    // `digest` permet de corréler avec les logs serveur en production.
    console.error(error);
  }, [error]);

  const t = COPY[lang];
  const dir = lang === "ar" ? "rtl" : "ltr";

  return (
    <html lang={lang} dir={dir}>
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "4rem 1rem",
          backgroundColor: "#000000",
          color: "#ffffff",
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        <main style={{ width: "100%", maxWidth: "36rem", textAlign: "start" }}>
          <h1
            style={{
              margin: "0 0 1rem",
              fontSize: "1.875rem",
              lineHeight: 1.2,
              fontWeight: 600,
              color: "#D4AF37",
            }}
          >
            {t.title}
          </h1>
          <p
            style={{
              margin: "0 0 2rem",
              fontSize: "1rem",
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.75)",
            }}
          >
            {t.body}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
            <button
              type="button"
              onClick={reset}
              style={{
                minHeight: "2.75rem",
                padding: "0.625rem 1.5rem",
                borderRadius: "0.5rem",
                border: "none",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#000000",
                backgroundColor: "#C4992D",
              }}
            >
              {t.retry}
            </button>
            <a
              href={t.homeHref}
              style={{
                minHeight: "2.75rem",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0.625rem 1.5rem",
                borderRadius: "0.5rem",
                border: "1px solid rgba(255,255,255,0.2)",
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "rgba(255,255,255,0.9)",
              }}
            >
              {t.home}
            </a>
          </div>
        </main>
      </body>
    </html>
  );
}
